import { useState } from 'react';
import DataTable from 'react-data-table-component';
import { Link, useNavigate } from 'react-router-dom';
import { useGetBanksQuery, useDeleteBankMutation } from '../../Redux/Service/BankApi';
import { Dropmenu } from '../../Components';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import StyledDashboardLayout from '../style';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useModuleHasPermissions } from '../../hooks/useModulePermissions';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   Grid,
   Typography,
   Divider,
   IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function BankList() {
   const MySwal = withReactContent(Swal);
   const navigate = useNavigate();
   const { data, isLoading } = useGetBanksQuery();
   const [deleteBank] = useDeleteBankMutation();
   const hasPermissions = useModuleHasPermissions('bank_account');

   const [selectedBank, setSelectedBank] = useState(null);
   const [viewModalOpen, setViewModalOpen] = useState(false);

   const banks = data?.response || [];

   const handleOpenView = (row) => {
      setSelectedBank(row);
      setViewModalOpen(true);
   };

   const handleCloseView = () => {
      setSelectedBank(null);
      setViewModalOpen(false);
   };

   const handleDelete = async (id) => {
      const result = await MySwal.fire({
         title: 'Are you sure?',
         text: 'Are you sure you want to delete this bank account?',
         icon: 'warning',
         showCancelButton: true,
         confirmButtonText: 'Yes, delete it!',
         cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) return;

      try {
         const res = await deleteBank(id);
         if (res?.error) {
            await MySwal.fire('Error', res.error?.data?.message || 'Delete failed', 'error');
         } else {
            await MySwal.fire('Deleted!', 'Bank account deleted successfully', 'success');
         }
      } catch (err) {
         await MySwal.fire('Error', err?.message || 'Delete failed', 'error');
      }
   };

   const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
         return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
         });
      } catch (e) {
         return dateString;
      }
   };

   const columns = [
      { name: 'SL No', cell: (_, i) => i + 1, width: '80px' },
      {
         name: 'Bank Name',
         selector: (r) => r.bank_name,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', fontWeight: 500 }}>
               {row.bank_name}
            </div>
         ),
      },
      {
         name: 'Account Number',
         selector: (r) => r.account_no,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{row.account_no}</div>
         ),
      },
      {
         name: 'Added At',
         selector: (r) => r.createdAt,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
               {formatDate(row.createdAt)}
            </div>
         ),
      },
      {
         name: 'Action',
         cell: (row) => (
            <Dropmenu
               isRolePermissionModule={true}
               handleView={() => handleOpenView(row)}
               handleEdit={() => navigate(`/bank/update/${row.id}`)}
               handleDelete={() => handleDelete(row.id)}
               canEdit={hasPermissions?.canUpdate}
               canDelete={hasPermissions?.canDelete}
            />
         ),
      },
   ];

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1>Bank Accounts</h1>
                     </div>
                     {hasPermissions?.canAdd && (
                        <div className="btnWrap">
                           <Link
                              to="/bank/create"
                              className="btn addBtn"
                              style={{ padding: 'inherit' }}
                           >
                              <PersonAddAlt1Icon style={{ marginRight: 6 }} />
                              Add Account
                           </Link>
                        </div>
                     )}
                  </div>
               </div>
            </div>
            <div className="card m-3 customCard">
               <div style={{ height: 10 }}></div>
               <DataTable columns={columns} data={banks} pagination progressPending={isLoading} />
            </div>
         </div>

         {/* Full View Modal */}
         <Dialog open={viewModalOpen} onClose={handleCloseView} fullWidth maxWidth="md">
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600 }}>
               Bank Account Full Details
               <IconButton onClick={handleCloseView} size="small">
                  <CloseIcon />
               </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 3 }}>
               {selectedBank && (
                  <div>
                     <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 2, color: '#1976d2' }}>
                        Core Information
                     </Typography>
                     <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item md={6} xs={12}>
                           <Typography variant="subtitle2" color="textSecondary">
                              Bank Name
                           </Typography>
                           <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedBank.bank_name || 'N/A'}
                           </Typography>
                        </Grid>
                        <Grid item md={6} xs={12}>
                           <Typography variant="subtitle2" color="textSecondary">
                              Account Number
                           </Typography>
                           <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedBank.account_no || 'N/A'}
                           </Typography>
                        </Grid>
                        {selectedBank.account_type && (
                           <Grid item md={6} xs={12}>
                              <Typography variant="subtitle2" color="textSecondary">
                                 Account Type
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                 {selectedBank.account_type}
                              </Typography>
                           </Grid>
                        )}
                        <Grid item md={6} xs={12}>
                           <Typography variant="subtitle2" color="textSecondary">
                              Added At
                           </Typography>
                           <Typography variant="body1">
                              {formatDate(selectedBank.createdAt)}
                           </Typography>
                        </Grid>
                     </Grid>

                     <Divider sx={{ my: 2 }} />

                     <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 2, color: '#1976d2' }}>
                        Additional Details (Custom Dynamic Fields)
                     </Typography>

                     {selectedBank.other_fields && Array.isArray(selectedBank.other_fields) && selectedBank.other_fields.length > 0 ? (
                        <Grid container spacing={2}>
                           {selectedBank.other_fields.map((field, idx) => (
                              <Grid item md={6} xs={12} key={idx}>
                                 <div style={{ backgroundColor: '#f8f9fa', padding: '10px 14px', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                       {field.label}
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word', mt: 0.5 }}>
                                       {field.value || 'N/A'}
                                    </Typography>
                                 </div>
                              </Grid>
                           ))}
                        </Grid>
                     ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                           No additional dynamic fields provided for this bank account.
                        </Typography>
                     )}
                  </div>
               )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
               <Button onClick={handleCloseView} variant="contained" size="small">
                  Close
               </Button>
            </DialogActions>
         </Dialog>
      </StyledDashboardLayout>
   );
}

export default BankList;
